import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { IAlbumImage, IAlbumImagesResponse } from "../../../hooks/albumImages";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { IAlbum } from "../../../hooks/albums";
import { Album3DRepresentationContainer, Album3DRepresentationContainerHandle } from "./Container";
import { toast } from "react-toastify";
import { useCurrentPublicCollection } from "../../../hooks/collections/useCollectionManager";
import { useGetAlbumImages } from "../../../hooks/albumImages/useGetAlbumImages";
import { useGetPublicAlbumImages } from "../../../hooks/albumImages/useGetPublicAlbumImages";
import { useFirstRender } from "../../../hooks/firstRender/useFirstRender";
import { useDropzone } from "react-dropzone";
import { strings } from "../../../localization/LocalizedStrings";

export const Album3DRepresentationFetcher = forwardRef<
  Album3DRepresentationContainerHandle,
  Album3DRepresentationFetcherProps
>(
  (
    {
      album,
      defaultFrontImage,
      isPublic,
      size,
      disabled,
      onImageChanged,
      isLoading,
      setIsLoading,
      setIsDragging,
      shouldOpenFileDialog
    },
    ref
  ) => {
    const [frontAlbumImage, setFrontAlbumImage] = useState<IAlbumImage>();
    const [albumImages, setAlbumImages] = useState<IAlbumImagesResponse>();
    const [currentPublicCollection] = useCurrentPublicCollection.useState();
    const { getAlbumImages } = useGetAlbumImages();
    const { getPublicAlbumImages } = useGetPublicAlbumImages();
    const firstRender = useFirstRender();
    const [imageFile, setImageFile] = useState<File & { preview: string }>();
    useEffect(() => {
      setImageFile(undefined);
    }, [isLoading]);
    const { getRootProps, getInputProps, open } = useDropzone({
      accept: { "image/*": [] },
      disabled: disabled,
      noClick: true,
      maxFiles: 1,
      onDrop: (acceptedFiles, filesRejection) => {
        if (filesRejection.length > 0) {
          toast.error(strings.upload_multiple_images_error);
          return;
        }
        const file = Object.assign(acceptedFiles[0], {
          preview: URL.createObjectURL(acceptedFiles[0])
        });
        setImageFile(file);
        onImageChanged(file);
      }
    });
    useEffect(() => {
      if (firstRender) return;
      open();
    }, [shouldOpenFileDialog]);
    useEffect(() => {
      if (imageFile) {
        return () => URL.revokeObjectURL(imageFile.preview);
      }
    }, [imageFile]);
    useEffect(() => {
      setFrontAlbumImage({
        uuid: album.uuid,
        kind: "additional",
        description: "",
        thumbnail: album.thumbnail,
        hasImage: album.hasImage
      });
    }, [album.uuid, album.hasImage, album.thumbnail]);
    const fetchCounter = useRef(0);
    const fetchAlbumImages = async () => {
      const fetchId = ++fetchCounter.current;
      setAlbumImages(undefined);
      const { status, body } = await getAlbumImages({ albumId: album.uuid });
      // A newer call has started since we did — discard unconditionally
      if (fetchId !== fetchCounter.current) return;
      if (StatusCodesHelper.isSuccessful(status)) {
        setAlbumImages(body);
      } else {
        toast.error(body.message);
      }
    };
    const fetchPublicAlbumImages = async () => {
      if (!currentPublicCollection) return;
      setAlbumImages(undefined);
      const { status, body } = await getPublicAlbumImages({
        albumId: album.uuid,
        collectionId: currentPublicCollection.uuid
      });
      if (StatusCodesHelper.isSuccessful(status)) {
        setAlbumImages(body);
      }
    };
    useEffect(() => {
      if (isPublic) {
        fetchPublicAlbumImages().then();
      } else {
        fetchAlbumImages().then();
      }
    }, [album.uuid]);

    const album3DRepresentationRef = useRef<Album3DRepresentationContainerHandle>(null);
    useImperativeHandle(ref, () => ({
      reset: () => {
        album3DRepresentationRef.current?.reset();
      },
      refreshImages: () => {
        fetchAlbumImages().then(album3DRepresentationRef.current?.refreshImages);
      }
    }));

    return (
      <div {...getRootProps()} style={{ display: "flex", width: "100%", height: "100%" }}>
        <Album3DRepresentationContainer
          ref={album3DRepresentationRef}
          frontCover={frontAlbumImage}
          backCover={albumImages?.back[0]}
          spine={albumImages?.spine[0]}
          size={size}
          defaultFrontImage={defaultFrontImage}
          isLoadingFrontCover={isLoading}
          setIsLoadingFrontCover={setIsLoading}
          setIsDragging={setIsDragging}
        />
        <input {...getInputProps()} />
      </div>
    );
  }
);

interface Album3DRepresentationFetcherProps {
  album: IAlbum;
  defaultFrontImage: string;
  isPublic: boolean;
  size: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setIsDragging: (isDragging: boolean) => void;
  shouldOpenFileDialog?: boolean;
  onImageChanged: (imageFile: File & { preview: string }) => void;
  disabled: boolean;
}
